import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import api from "../lib/api";
import { undoableDelete } from "../lib/undo";
import { toast } from "sonner";
import { Document, Page, pdfjs } from "react-pdf";
import mammoth from "mammoth";
import * as XLSX from "xlsx";
import DOMPurify from "dompurify";
import { Download, Loader2, FileWarning, ZoomIn, ZoomOut, RotateCw, MessageSquarePlus, Highlighter, Trash2, MapPin, CornerDownRight, Send, ThumbsUp, CheckCircle2, Circle, Filter, Mail } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const IMG_EXT = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];
const TEXT_EXT = ["txt", "csv", "md", "json", "log", "js", "py", "html", "css", "xml"];
const ext = (fn) => (fn.split(".").pop() || "").toLowerCase();
const EMPTY_ANNS = [];
const HL_SOLID = { green: "#2E7D32", amber: "#B8860B", red: "#B91C1C", blue: "#1565C0" };
const HL_RGBA = { green: "rgba(46,125,50,0.30)", amber: "rgba(255,214,0,0.40)", red: "rgba(185,28,28,0.26)", blue: "rgba(21,101,192,0.24)" };
const HL_ORDER = ["green", "amber", "red", "blue"];
const HL_LABEL = { green: "Good", amber: "Fix", red: "Issue", blue: "Note" };

export const DocViewer = ({ open, onClose, path, fileName = "document", contentType = "",
                            subId, annotations = EMPTY_ANNS, canAnnotate = false, canReply = false, onAnnotationsChange, fileIdx = 0 }) => {
  const [blobUrl, setBlobUrl] = useState("");
  const [text, setText] = useState("");
  const [html, setHtml] = useState("");
  const [thumbUrl, setThumbUrl] = useState("");
  const [kind, setKind] = useState("loading");
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [anns, setAnns] = useState([]);
  const [mode, setMode] = useState("");            // "" | "pin" | "highlight"
  const [pending, setPending] = useState(null);    // {type,page,x,y,w,h}
  const [pendingText, setPendingText] = useState("");
  const [drag, setDrag] = useState(null);          // {page,x0,y0,x1,y1}
  const [replyDraft, setReplyDraft] = useState({});
  const [hlColor, setHlColor] = useState("amber");
  const [unresolvedOnly, setUnresolvedOnly] = useState(false);
  const [colorFilter, setColorFilter] = useState("");
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => { setAnns((annotations || []).filter((a) => (a.file_idx || 0) === fileIdx)); }, [annotations, open, fileIdx]);

  useEffect(() => {
    if (!open || !path) return;
    let revoke;
    (async () => {
      setKind("loading"); setText(""); setHtml(""); setThumbUrl(""); setScale(1); setRotate(0);
      setNumPages(0); setPending(null); setMode(""); setDrag(null);
      const e = ext(fileName);
      try {
        const res = await api.get(path, { responseType: "blob" });
        const ct = (res.data.type || contentType || "").toLowerCase();
        const bu = window.URL.createObjectURL(res.data); revoke = bu; setBlobUrl(bu);
        const ab = await res.data.arrayBuffer();
        if (ct.includes("pdf") || e === "pdf") setKind("pdf");
        else if (ct.startsWith("image/") || IMG_EXT.includes(e)) setKind("image");
        else if (e === "docx" || ct.includes("wordprocessingml")) {
          const r = await mammoth.convertToHtml({ arrayBuffer: ab }); setHtml(r.value || "<p>(empty document)</p>"); setKind("docx");
        }
        else if (["xlsx", "xls"].includes(e) || ct.includes("spreadsheetml") || ct.includes("ms-excel")) {
          const wb = XLSX.read(ab, { type: "array" });
          setHtml(wb.SheetNames.map((n) => `<h4 style="font-weight:700;margin:12px 0 4px">${n}</h4>` + XLSX.utils.sheet_to_html(wb.Sheets[n])).join("")); setKind("excel");
        }
        else if (["pptx", "ppt"].includes(e) || ct.includes("presentationml") || ct.includes("ms-powerpoint")) {
          setKind("ppt"); loadPptThumb();
        }
        else if (ct.startsWith("text/") || ct.includes("json") || TEXT_EXT.includes(e)) {
          if (e === "csv") { const wb = XLSX.read(ab, { type: "array" }); setHtml(XLSX.utils.sheet_to_html(wb.Sheets[wb.SheetNames[0]])); setKind("excel"); }
          else { setText(await res.data.text()); setKind("text"); }
        }
        else setKind("other");
      } catch (err) { setKind("error"); }
    })();
    return () => { if (revoke) setTimeout(() => window.URL.revokeObjectURL(revoke), 1500); };
  }, [open, path, fileName, contentType]); // eslint-disable-line

  const loadPptThumb = async () => {
    const url = subId ? `/assignment-submissions/${subId}/ppt-thumbnail`
      : path.startsWith("/media/") ? `${path}/ppt-thumbnail` : "";
    if (!url) return;
    try {
      const res = await api.get(url, { responseType: "blob" });
      setThumbUrl(window.URL.createObjectURL(res.data));
    } catch (e) { /* no embedded thumbnail — plain download prompt stays */ }
  };

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a"); a.href = blobUrl; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const rel = (ev) => {
    const r = ev.currentTarget.getBoundingClientRect();
    return { x: (ev.clientX - r.left) / r.width, y: (ev.clientY - r.top) / r.height };
  };

  const onPageMouseDown = (page) => (ev) => {
    if (mode !== "highlight" || !canAnnotate) return;
    const p = rel(ev); setDrag({ page, x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  };
  const onPageMouseMove = (page) => (ev) => {
    if (mode !== "highlight" || !drag || drag.page !== page) return;
    const p = rel(ev); setDrag((d) => ({ ...d, x1: p.x, y1: p.y }));
  };
  const onPageMouseUp = (page) => () => {
    if (mode !== "highlight" || !drag || drag.page !== page) return;
    const x = Math.min(drag.x0, drag.x1), y = Math.min(drag.y0, drag.y1);
    const w = Math.abs(drag.x1 - drag.x0), h = Math.abs(drag.y1 - drag.y0);
    setDrag(null);
    if (w < 0.02 || h < 0.01) return;
    setPending({ type: "highlight", page, x, y, w, h }); setPendingText("");
  };
  const onPageClick = (page) => (ev) => {
    if (mode !== "pin" || !canAnnotate) return;
    const p = rel(ev); setPending({ type: "pin", page, x: p.x, y: p.y, w: 0, h: 0 }); setPendingText("");
  };

  const savePin = async () => {
    if (!pending || !pendingText.trim()) { setPending(null); return; }
    try {
      const body = { ...pending, text: pendingText.trim(), file_idx: fileIdx };
      if (pending.type === "highlight") body.color = hlColor;
      const { data } = await api.post(`/assignment-submissions/${subId}/annotations`, body);
      setAnns((a) => [...a, data]); setPending(null); setMode("");
      toast.success(pending.type === "highlight" ? "Highlight added" : "Comment added");
      onAnnotationsChange && onAnnotationsChange();
    } catch (err) { toast.error("Could not save"); }
  };

  const delPin = (id) => {
    const a = anns.find((x) => x.id === id);
    setAnns((s) => s.filter((x) => x.id !== id));
    undoableDelete({
      label: "Feedback", entityType: "feedback",
      perform: async () => { await api.delete(`/assignment-submissions/${subId}/annotations/${id}`); onAnnotationsChange && onAnnotationsChange(); },
      onUndo: () => setAnns((s) => (a && !s.some((x) => x.id === a.id) ? [...s, a] : s)),
    });
  };

  const patchState = async (annId, patch) => {
    setAnns((a) => a.map((x) => x.id === annId ? { ...x, ...patch } : x));   // optimistic
    try { await api.post(`/assignment-submissions/${subId}/annotations/${annId}/state`, patch); onAnnotationsChange && onAnnotationsChange(); }
    catch (err) { toast.error("Could not update"); }
  };

  const sendReply = async (annId) => {
    const txt = (replyDraft[annId] || "").trim();
    if (!txt) return;
    try {
      const { data } = await api.post(`/assignment-submissions/${subId}/annotations/${annId}/reply`, { text: txt });
      setAnns((a) => a.map((x) => x.id === annId ? { ...x, replies: [...(x.replies || []), data] } : x));
      setReplyDraft((d) => ({ ...d, [annId]: "" }));
      onAnnotationsChange && onAnnotationsChange();
    } catch (err) { toast.error("Could not send reply"); }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post(`/assignment-submissions/${subId}/feedback-digest`);
      toast.success(data.channel === "email" ? `Feedback summary emailed to the learner (${data.comments} note${data.comments === 1 ? "" : "s"})` : `Feedback summary sent to the learner's portal (${data.comments} note${data.comments === 1 ? "" : "s"})`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send summary"); }
    finally { setSendingDigest(false); }
  };

  const zoomable = kind === "image" || kind === "pdf";
  const pdfWidth = Math.round(760 * scale);
  const dragRect = drag && { left: `${Math.min(drag.x0, drag.x1) * 100}%`, top: `${Math.min(drag.y0, drag.y1) * 100}%`, width: `${Math.abs(drag.x1 - drag.x0) * 100}%`, height: `${Math.abs(drag.y1 - drag.y0) * 100}%` };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-head text-xl truncate pr-8">{fileName}</DialogTitle>
          <DialogDescription>Preview the attached document here without downloading it.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 mb-2">
          {zoomable && <>
            <button data-testid="dv-zoom-out" onClick={() => setScale((s) => Math.max(0.5, +(s - 0.25).toFixed(2)))} className="p-2 rounded-full border hover:bg-[#EEF0F2]" style={{ borderColor: "var(--sj-border)" }}><ZoomOut className="h-4 w-4" /></button>
            <span className="text-xs w-10 text-center" style={{ color: "var(--sj-text-3)" }}>{Math.round(scale * 100)}%</span>
            <button data-testid="dv-zoom-in" onClick={() => setScale((s) => Math.min(3, +(s + 0.25).toFixed(2)))} className="p-2 rounded-full border hover:bg-[#EEF0F2]" style={{ borderColor: "var(--sj-border)" }}><ZoomIn className="h-4 w-4" /></button>
            <button data-testid="dv-rotate" onClick={() => setRotate((r) => (r + 90) % 360)} className="p-2 rounded-full border hover:bg-[#EEF0F2]" style={{ borderColor: "var(--sj-border)" }}><RotateCw className="h-4 w-4" /></button>
          </>}
          {kind === "pdf" && canAnnotate && subId && (<>
            <button data-testid="dv-annotate-toggle" onClick={() => setMode((v) => v === "pin" ? "" : "pin")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border" style={mode === "pin" ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { borderColor: "var(--sj-primary)", color: "var(--sj-primary)" }}>
              <MessageSquarePlus className="h-4 w-4" /> {mode === "pin" ? "Click the page" : "Add comment"}
            </button>
            <button data-testid="dv-highlight-toggle" onClick={() => setMode((v) => v === "highlight" ? "" : "highlight")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border" style={mode === "highlight" ? { background: HL_SOLID[hlColor], color: "#fff", borderColor: HL_SOLID[hlColor] } : { borderColor: "#B8860B", color: "#8a6508" }}>
              <Highlighter className="h-4 w-4" /> {mode === "highlight" ? "Drag over text" : "Highlight"}
            </button>
            {mode === "highlight" && (
              <div className="inline-flex items-center gap-1.5" data-testid="dv-highlight-colors">
                {HL_ORDER.map((c) => (
                  <button key={c} data-testid={`dv-color-${c}`} title={HL_LABEL[c]} onClick={() => setHlColor(c)} className="rounded-full transition-transform" style={{ width: 20, height: 20, background: HL_SOLID[c], border: hlColor === c ? "2px solid #111" : "2px solid #fff", boxShadow: "0 0 0 1px var(--sj-border)", transform: hlColor === c ? "scale(1.15)" : "scale(1)" }} />
                ))}
              </div>
            )}
          </>)}
          <div className="ml-auto flex items-center gap-2">
            {kind === "pdf" && canAnnotate && subId && anns.length > 0 && (
              <button data-testid="feedback-digest-btn" onClick={sendDigest} disabled={sendingDigest} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border hover:bg-[#EEF0F2] disabled:opacity-50" style={{ borderColor: "var(--sj-primary)", color: "var(--sj-primary)" }}><Mail className="h-4 w-4" /> {sendingDigest ? "Sending…" : "Email summary"}</button>
            )}
            <button data-testid="doc-viewer-download" onClick={download} disabled={!blobUrl} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-white text-sm font-semibold hover:brightness-105 disabled:opacity-50" style={{ background: "var(--sj-primary)" }}><Download className="h-4 w-4" /> Download</button>
          </div>
        </div>

        <div className="overflow-auto rounded-lg" style={{ maxHeight: "68vh", background: "#F1F1F1" }} data-testid="doc-viewer-body">
          {kind === "loading" && <div className="py-20 flex flex-col items-center gap-2 text-sm" style={{ color: "var(--sj-text-3)" }}><Loader2 className="h-6 w-6 animate-spin" /> Loading preview…</div>}
          {kind === "error" && <div className="py-20 flex flex-col items-center gap-2 text-sm" style={{ color: "var(--sj-text-3)" }}><FileWarning className="h-6 w-6" /> Couldn't load this file. Try downloading it.</div>}
          {kind === "other" && <div className="py-16 text-center text-sm" style={{ color: "var(--sj-text-3)" }}>This file type can't be previewed in-app. Use Download to open it.</div>}
          {kind === "ppt" && (
            <div className="py-8 flex flex-col items-center gap-3 text-center" data-testid="doc-viewer-ppt">
              {thumbUrl
                ? <img src={thumbUrl} alt="First slide preview" data-testid="doc-viewer-ppt-thumb" className="max-w-full rounded-lg shadow border" style={{ maxHeight: "50vh", borderColor: "var(--sj-border)" }} />
                : <FileWarning className="h-8 w-8" style={{ color: "var(--sj-text-3)" }} />}
              <div className="text-sm max-w-sm" style={{ color: "var(--sj-text-3)" }}>{thumbUrl ? "First-slide preview. Download to view all slides." : "PowerPoint slides can't be previewed inline. Download the file to open it in PowerPoint or Keynote."}</div>
              <button data-testid="ppt-download-btn" onClick={download} disabled={!blobUrl} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-white text-sm font-semibold hover:brightness-105 disabled:opacity-50" style={{ background: "var(--sj-primary)" }}><Download className="h-4 w-4" /> Download slides</button>
            </div>
          )}
          {kind === "image" && <div className="p-4 flex justify-center"><img src={blobUrl} alt={fileName} data-testid="doc-viewer-image" style={{ transform: `scale(${scale}) rotate(${rotate}deg)`, transition: "transform 0.15s", maxWidth: "100%" }} /></div>}
          {kind === "text" && <pre data-testid="doc-viewer-text" className="text-sm whitespace-pre-wrap p-4 bg-white" style={{ color: "var(--sj-text-2)" }}>{text}</pre>}
          {(kind === "docx" || kind === "excel") && <div data-testid="doc-viewer-office-html" className="bg-white p-6 text-sm" style={{ color: "var(--sj-text-2)" }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />}
          {kind === "pdf" && (
            <div className="flex flex-col items-center py-3">
              <Document file={blobUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)} loading={<div className="py-16 text-sm" style={{ color: "var(--sj-text-3)" }}>Rendering PDF…</div>} error={<div className="py-16 text-sm" style={{ color: "var(--sj-text-3)" }}>Couldn't render PDF — try Download.</div>}>
                {Array.from({ length: numPages }, (_, i) => i + 1).map((pg) => (
                  <div key={pg} className="relative mb-3 shadow select-none" style={{ cursor: mode === "pin" ? "crosshair" : mode === "highlight" ? "cell" : "default" }}
                       onClick={onPageClick(pg)} onMouseDown={onPageMouseDown(pg)} onMouseMove={onPageMouseMove(pg)} onMouseUp={onPageMouseUp(pg)}>
                    <Page pageNumber={pg} width={pdfWidth} rotate={rotate} renderTextLayer={false} renderAnnotationLayer={false} />
                    {anns.filter((a) => a.page === pg && a.type === "highlight").map((a) => (
                      <div key={a.id} title={a.text} data-testid={`pdf-highlight-${a.id}`} className="absolute rounded-sm" style={{ left: `${a.x * 100}%`, top: `${a.y * 100}%`, width: `${(a.w || 0) * 100}%`, height: `${(a.h || 0) * 100}%`, background: HL_RGBA[a.color] || HL_RGBA.amber, border: `1px solid ${HL_SOLID[a.color] || HL_SOLID.amber}`, opacity: a.resolved ? 0.4 : 1, pointerEvents: "none" }} />
                    ))}
                    {anns.filter((a) => a.page === pg && a.type !== "highlight").map((a) => (
                      <div key={a.id} title={a.text} data-testid={`pdf-pin-${a.id}`} className="absolute -translate-x-1/2 -translate-y-full flex items-center justify-center rounded-full text-white shadow" style={{ left: `${a.x * 100}%`, top: `${a.y * 100}%`, width: 22, height: 22, background: "#B91C1C", pointerEvents: "none" }}>
                        <MapPin className="h-3.5 w-3.5" />
                      </div>
                    ))}
                    {drag && drag.page === pg && <div className="absolute rounded-sm" style={{ ...dragRect, background: HL_RGBA[hlColor], border: `1px dashed ${HL_SOLID[hlColor]}`, pointerEvents: "none" }} />}
                    {pending && pending.page === pg && (
                      <div className="absolute z-20 bg-white rounded-xl shadow-lg border p-2 w-56" style={{ left: `${pending.x * 100}%`, top: `${(pending.y + (pending.h || 0)) * 100}%`, borderColor: "var(--sj-primary)" }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        <textarea data-testid="pin-text-input" autoFocus rows={2} value={pendingText} onChange={(e) => setPendingText(e.target.value)} placeholder={pending.type === "highlight" ? "Note on this passage…" : "Your comment…"} className="w-full text-sm border rounded-lg p-1.5 outline-none" style={{ borderColor: "var(--sj-border)" }} />
                        <div className="flex justify-end gap-1 mt-1">
                          <button onClick={() => setPending(null)} className="text-xs px-2 py-1 rounded-full border" style={{ borderColor: "var(--sj-border)" }}>Cancel</button>
                          <button data-testid="pin-save-btn" onClick={savePin} className="text-xs px-3 py-1 rounded-full text-white font-semibold" style={{ background: "var(--sj-primary)" }}>Save</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </Document>
            </div>
          )}
        </div>

        {kind === "pdf" && anns.length > 0 && (
          <div className="mt-2 max-h-56 overflow-auto pr-1" data-testid="pdf-comments-list">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5 sticky top-0 bg-white pb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--sj-text-3)" }}>Feedback</span>
                <div className="flex items-center gap-1.5" data-testid="doc-viewer-color-legend">
                  {HL_ORDER.map((c) => (
                    <button key={c} data-testid={`color-filter-${c}`} onClick={() => setColorFilter((v) => v === c ? "" : c)} title={`Show only ${HL_LABEL[c]}`}
                      className="inline-flex items-center gap-1 text-[11px] rounded-full px-1.5 py-0.5 border transition-colors"
                      style={colorFilter === c ? { borderColor: HL_SOLID[c], background: HL_RGBA[c], color: "var(--sj-text-2)" } : { borderColor: "transparent", color: "var(--sj-text-3)" }}>
                      <span className="inline-block rounded-sm" style={{ width: 10, height: 10, background: HL_SOLID[c] }} />{HL_LABEL[c]}
                    </button>
                  ))}
                  {colorFilter && <button data-testid="color-filter-clear" onClick={() => setColorFilter("")} className="text-[11px] underline" style={{ color: "var(--sj-primary)" }}>All</button>}
                </div>
              </div>
              {canAnnotate && (() => { const unresolved = anns.filter((a) => !a.resolved).length; return (
                <button data-testid="unresolved-filter-toggle" onClick={() => setUnresolvedOnly((v) => !v)} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 border" style={unresolvedOnly ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { borderColor: "var(--sj-border)", color: "var(--sj-text-3)" }}>
                  <Filter className="h-3.5 w-3.5" /> {unresolvedOnly ? "Showing unresolved" : `Unresolved (${unresolved})`}
                </button>
              ); })()}
            </div>
            {(() => { const shown = anns.filter((a) => (!unresolvedOnly || !a.resolved) && (!colorFilter || (a.color || "red") === colorFilter)); return shown.length === 0 ? (
              <div className="py-4 text-center text-xs" data-testid="feedback-all-resolved" style={{ color: "var(--sj-text-3)" }}>{colorFilter ? `No ${HL_LABEL[colorFilter]} feedback.` : "All feedback resolved 🎉"}</div>
            ) : shown.map((a, i) => (
              <div key={a.id} className="py-1.5 border-b" style={{ borderColor: "#F0EEE9" }}>
                <div className="flex items-start gap-2 text-sm">
                  <span className="mt-0.5 shrink-0 inline-flex items-center justify-center rounded-full text-white text-[10px] font-bold" style={{ width: 18, height: 18, background: a.type === "highlight" ? (HL_SOLID[a.color] || HL_SOLID.amber) : "#B91C1C" }}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <span style={a.resolved ? { textDecoration: "line-through", color: "var(--sj-text-3)" } : {}}>{a.text}</span>
                    <span className="text-xs" style={{ color: "var(--sj-text-3)" }}> · {a.type === "highlight" ? `${HL_LABEL[a.color] || "highlight"} highlight` : "pin"} · p{a.page} · {a.by}</span>
                    {a.resolved && <span data-testid={`resolved-badge-${a.id}`} className="ml-1 text-[10px] font-bold uppercase rounded px-1.5 py-0.5" style={{ background: "#E3F1EC", color: "var(--sj-primary)" }}>Done{a.resolved_by ? ` · ${a.resolved_by}` : ""}</span>}
                    {a.liked && <ThumbsUp data-testid={`liked-badge-${a.id}`} className="inline h-3.5 w-3.5 ml-1" style={{ color: "var(--sj-primary)" }} />}
                    {(a.replies || []).map((r) => (
                      <div key={r.id} data-testid={`reply-${r.id}`} className="flex items-start gap-1.5 mt-1 ml-1 text-[13px]">
                        <CornerDownRight className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "var(--sj-text-3)" }} />
                        <div><span>{r.text}</span> <span className="text-xs" style={{ color: r.by_role === "tutor" ? "var(--sj-primary)" : "var(--sj-text-3)" }}>· {r.by}{r.by_role === "tutor" ? " (Tutor)" : ""}</span></div>
                      </div>
                    ))}
                    {canReply && subId && (
                      <div className="flex items-center gap-1.5 mt-1.5 ml-1">
                        <input data-testid={`reply-input-${a.id}`} value={replyDraft[a.id] || ""} onChange={(e) => setReplyDraft((d) => ({ ...d, [a.id]: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") sendReply(a.id); }} placeholder="Reply…" className="flex-1 text-[13px] border rounded-full px-3 py-1 outline-none" style={{ borderColor: "var(--sj-border)" }} />
                        <button data-testid={`reply-send-${a.id}`} onClick={() => sendReply(a.id)} disabled={!(replyDraft[a.id] || "").trim()} className="p-1.5 rounded-full text-white disabled:opacity-40" style={{ background: "var(--sj-primary)" }}><Send className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                    {canReply && !canAnnotate && subId && (
                      <div className="flex items-center gap-3 mt-1 ml-1">
                        <button data-testid={`resolve-toggle-${a.id}`} onClick={() => patchState(a.id, { resolved: !a.resolved })} className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: a.resolved ? "var(--sj-primary)" : "var(--sj-text-3)" }}>
                          {a.resolved ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />} {a.resolved ? "Marked done" : "Mark as done"}
                        </button>
                        <button data-testid={`like-toggle-${a.id}`} onClick={() => patchState(a.id, { liked: !a.liked })} className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: a.liked ? "var(--sj-primary)" : "var(--sj-text-3)" }}>
                          <ThumbsUp className="h-3.5 w-3.5" fill={a.liked ? "currentColor" : "none"} /> {a.liked ? "Liked" : "Thumbs up"}
                        </button>
                      </div>
                    )}
                  </div>
                  {canAnnotate && <button data-testid={`pin-del-${a.id}`} onClick={() => delPin(a.id)} className="opacity-60 hover:opacity-100"><Trash2 className="h-3.5 w-3.5" style={{ color: "#B91C1C" }} /></button>}
                </div>
              </div>
            )); })()}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocViewer;
